import { NOTIFICATION_NODE_TYPE } from "@/lib/workflow/notificationChannels";
import { getRequiredConnectorType } from "@/utils/workflowTransform";

// The "notification" virtual type is resolved at template install time and has
// no action-registry entry, so inject a fallback icon so it still renders
// consistently in canvas, template previews, step lists, etc.
const NOTIFICATION_LOGO_URL = "https://ai-icon.b3.fun/logo/notification";

interface DefinitionLike {
  type: string;
  logoUrl?: string;
  connector?: { type: string };
}

interface ConnectorTypeLike {
  type: string;
  logoUrl?: string;
}

export interface ResolvedNodeVisual<TDefinition extends DefinitionLike = DefinitionLike> {
  definition?: TDefinition;
  logoUrl?: string;
  connector?: {
    type: string;
    logoUrl?: string;
  };
}

export function getTypeNamespace(type: string): string | null {
  const idx = type.indexOf("-");
  if (idx <= 0) return null;
  return type.slice(0, idx).toLowerCase();
}

export function resolveNodeDefinition<TDefinition extends DefinitionLike>(
  type: string,
  definitions: TDefinition[],
): TDefinition | undefined {
  const exact = definitions.find(def => def.type === type);
  if (exact) return exact;

  const namespace = getTypeNamespace(type);
  if (!namespace) return undefined;

  return definitions.find(def => {
    const defNamespace = getTypeNamespace(def.type);
    return defNamespace === namespace && (!!def.logoUrl || !!def.connector?.type);
  });
}

export function resolveNodeVisual<TDefinition extends DefinitionLike>(
  type: string,
  definitions: TDefinition[],
  connectorTypes: ConnectorTypeLike[],
): ResolvedNodeVisual<TDefinition> {
  if (type === NOTIFICATION_NODE_TYPE) {
    return { logoUrl: NOTIFICATION_LOGO_URL };
  }
  const definition = resolveNodeDefinition(type, definitions);

  // Only trust logoUrl and connector from exact definition matches.
  // Namespace (prefix) matches can return completely unrelated actions
  // (e.g. "send-slack-message" → "send-native-token") whose visual
  // properties would be wrong.
  const isExactMatch = definition?.type === type;
  const requiredConnectorType = getRequiredConnectorType(type, isExactMatch ? definition : undefined);

  const connector = requiredConnectorType
    ? {
        type: requiredConnectorType,
        logoUrl: connectorTypes.find(ct => ct.type === requiredConnectorType)?.logoUrl,
      }
    : undefined;

  return {
    definition,
    logoUrl: (isExactMatch && definition?.logoUrl) || undefined,
    connector,
  };
}
